/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import {
  DataTable,
  Loader,
  SdiLicense,
  SdiProfile,
  SdirButton,
  SdirInput,
  SdirModal,
  TextLink,
} from '@sdir/sds';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { CustomStatusLabel, ErrorWithTrace } from '@sdir/blueprint.aps/lib/components';
import { formatDateString } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
// eslint-disable-next-line max-len
import { CustomStatusLabelStatus } from '@sdir/blueprint.aps/lib/components/Atoms/CustomStatusLabel';
// eslint-disable-next-line max-len
import { V1SeafareridentitydocumentInvalidateBodyPutRequest } from '@sdir/httpclient/lib/clients/aps/personalcertificate';
import { PageHeader } from '../../Atoms';
import { routes } from '../../../constants/routes';
import {
  useCertificateCodes,
  useCurrentPerson,
  useCurrentRegisteredPerson,
  useFeatureToggles,
} from '../../../services/hooks';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { isValidNorwegianPnr } from '../../../helpers/userHelpers';
import { getApplicationApi, sidCertificateApi } from '../../../httpclient';
import {
  getSimplifiedApplicationStatus,
  mapToCustomStatusLabel,
} from '../../../helpers/applicationHelpers';
import { SidApplicationsHeaders, SidCertificateHeaders } from '../../../helpers/TableHeaders';
import { GetCertificateNameByCode } from '../../../helpers/Certificates';
import { ToggleDisableSid } from '../../../constants/permissions';
import { getReceiptLanguage } from '../../../helpers/languageHelpers';

const SidOverviewPage: React.FC = () => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const [receiptError, setReceiptError] = useState<any>();
  const [documentNumber, setDocumentNumber] = useState<string>();
  const [showConfirmLost, setShowConfirmLost] = useState<boolean>(false);
  const [lostReason, setLostReason] = useState<string>('');
  const navigate = useNavigateWithLang();
  const featureToggle = useFeatureToggles();
  const codes = useCertificateCodes();
  const {
    person: { result: contactInfo },
  } = useCurrentPerson();
  const {
    person: { result: registeredPerson },
  } = useCurrentRegisteredPerson();
  const enabledSid = !isToggleOn(
    featureToggle?.result,
    ToggleDisableSid,
    contactInfo?.personalIdentificationNumber || ''
  );

  const {
    errorObject: sidCertificatesError,
    result: sidCertificates,
    loading: sidCertificatesLoading,
    callApi: getSidCertificates,
  } = useApi({
    apiFunction: (p) => sidCertificateApi.v1SeafareridentitydocumentSearchUserGet(1, 1000, p),
    callApiOnInit: false,
  });

  const {
    errorObject: reportError,
    loading: reportLoading,
    callApi: reportSidAsLost,
  } = useApi({
    apiFunction: (p) =>
      sidCertificateApi.v1SeafareridentitydocumentMarkaslostBodyPut(p.documentNumber, p.body),
    callApiOnInit: false,
    successCallback: () => {
      setShowConfirmLost(false);
      setDocumentNumber(undefined);
      setLostReason('');
      if (contactInfo?.personalIdentificationNumber) {
        getSidCertificates(contactInfo.personalIdentificationNumber);
      }
    },
  });

  const { result: applications, loading: applicationsLoading } = useApi({
    apiFunction: () =>
      getApplicationApi.v2GetGetownapplicationsQueryPost({
        filter: {
          status: undefined,
          freeTextSearchStringOnType: undefined,
          applicationTypes: undefined,
          certificateCode: 'SID',
        },
      }),
    callApiOnInit: true,
  });

  const downloadPaymentReceipt = useCallback(
    (paymentId, paidDate) => {
      const token = currentUser?.access_token || '';
      const url = `${process.env.REACT_APP_API_URL_PAYMENT_WINDOW}payment/receiptDocument`;
      setReceiptError(undefined);
      axios
        .post(
          url,
          {
            id: paymentId,
            language: { language: getReceiptLanguage(intl?.locale || '') },
          },
          {
            responseType: 'blob',
            headers: {
              'Accept-Language': intl?.locale || 'no',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        .then((response: AxiosResponse) => {
          if (response.status >= 400 && response.status < 600) {
            const err = new Error(response.statusText);
            setReceiptError(err);
            throw err;
          }
          const bloburl = window.URL.createObjectURL(
            new Blob([response.data], { type: 'application/octet-stream' })
          );
          return bloburl;
        })
        .then((blobUrl: string) => {
          const link = document.createElement('a');
          link.href = blobUrl;
          const paymentDate = paidDate?.substring(0, 10);
          const filename = `Payment_Receipt_${paymentDate}.pdf`;
          link.setAttribute('download', filename);

          const clickHandler = () => {
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
              link.removeEventListener('click', clickHandler);
              document.body.removeChild(link);
            }, 150);
          };

          document.body.appendChild(link);
          link.addEventListener('click', clickHandler, false);
          link.click();
        })
        .catch((e: AxiosError) => {
          setReceiptError(e);
        });
    },
    [currentUser, intl?.locale]
  );

  const onReportLost = (documentNr?: string) => {
    setDocumentNumber(documentNr);
    setShowConfirmLost(true);
  };

  const onCloseConfirmLost = () => {
    setDocumentNumber(undefined);
    setLostReason('');
    setShowConfirmLost(false);
  };

  const onConfirmLost = () => {
    const body: V1SeafareridentitydocumentInvalidateBodyPutRequest = {
      reason: lostReason || '',
    };
    const payload = {
      documentNumber,
      body,
    };
    reportSidAsLost(payload);
  };

  const hasValidPnr = useMemo(() => {
    return isValidNorwegianPnr(
      contactInfo?.personalIdentificationNumber || registeredPerson?.personNumber || ''
    );
  }, [contactInfo, registeredPerson]);

  const allCertificates = useMemo(() => {
    const items = sidCertificates?.result || [];
    return items.map((c) => {
      const labelStatus = c.isValid
        ? CustomStatusLabelStatus.Done
        : CustomStatusLabelStatus.Rejected;
      const statusText = c.isValid
        ? intl.formatMessage({ id: 'certificates.table.status.valid' })
        : intl.formatMessage({ id: 'certificates.table.status.invalid' });
      return {
        documentNumber: c?.documentNumber || '',
        status: <CustomStatusLabel status={labelStatus} text={statusText} />,
        issuedDate: formatDateString(c?.issuedDate || ''),
        expiryDate: formatDateString(c?.expiryDate || ''),
        actions: c?.isValid ? (
          <ActionsWrapper>
            <SdirButton
              text={intl.formatMessage({ id: 'certificates.button.reportlost' })}
              onClick={() => onReportLost(c?.documentNumber)}
              size="sm"
            />
          </ActionsWrapper>
        ) : (
          <></>
        ),
      };
    });
  }, [sidCertificates?.result]);

  const allApplications = useMemo(() => {
    const items = applications?.result || [];
    return items.map((a) => {
      const simpleStatus = getSimplifiedApplicationStatus(a);
      const link = generatePath(routes.application.details, { applicationUid: a.uid || '' });
      const labelStatus = mapToCustomStatusLabel(a);
      return {
        status: (
          <CustomStatusLabel
            status={labelStatus}
            text={intl.formatMessage({ id: `application.tile.status.${simpleStatus}` })}
          />
        ),
        certificate: GetCertificateNameByCode(intl?.locale, a.certificate, codes.result) || '',
        created: formatDateString(a.applicationDate || ''),
        actions: (
          <ActionsWrapper>
            {a.payment?.paymentId ? (
              <TextLink
                text={intl.formatMessage({ id: 'certificates.button.receipt' })}
                onClick={() => downloadPaymentReceipt(a.payment?.paymentId, a.payment?.paidDate)}
              />
            ) : (
              <></>
            )}
            <SdirButton
              text={intl.formatMessage({ id: 'certificates.button.showapplication' })}
              onClick={() => navigate(link)}
              size="sm"
            />
          </ActionsWrapper>
        ),
      };
    });
  }, [applications?.result, codes?.result, intl?.locale]);

  const backToHome = () => {
    navigate(routes.home);
  };

  const startNewApplication = () => {
    navigate(routes.newSidApplication);
  };

  useEffect(() => {
    if (contactInfo?.personalIdentificationNumber) {
      getSidCertificates(contactInfo?.personalIdentificationNumber);
    }
  }, [contactInfo?.personalIdentificationNumber]);

  if (!hasValidPnr || !enabledSid) return <></>;

  return (
    <Wrapper>
      <PageHeader
        backText={intl.formatMessage({ id: 'back.to.dashboard' })}
        heading={intl.formatMessage({ id: 'sidDashboard.page.title' })}
        primaryIcon={SdiLicense}
        secondaryIcon={SdiProfile}
        backClick={backToHome}
      />
      <ButtonRow>
        <SdirButton
          type="primary"
          text={intl.formatMessage({ id: 'sidOverview.button.newapplication' })}
          onClick={startNewApplication}
        />
      </ButtonRow>
      {receiptError && (
        <ErrorWrapper>
          <ErrorMessage error={receiptError} />
        </ErrorWrapper>
      )}
      {applicationsLoading || sidCertificatesLoading ? <Loader /> : <></>}
      {sidCertificatesError && (
        <FeedbackWrapper>
          <ErrorWithTrace
            error={sidCertificatesError}
            trace={sidCertificatesError?.response?.headers?.traceresponse}
          />
        </FeedbackWrapper>
      )}
      {allCertificates && allCertificates.length > 0 ? (
        <TableWrapper>
          <StyledDataTable
            data={allCertificates || []}
            columns={SidCertificateHeaders(intl.formatMessage)}
          />
        </TableWrapper>
      ) : (
        <></>
      )}
      {allApplications && allApplications.length > 0 ? (
        <TableWrapper>
          <StyledDataTable
            data={allApplications || []}
            columns={SidApplicationsHeaders(intl.formatMessage)}
          />
        </TableWrapper>
      ) : (
        <></>
      )}
      <SdirModal
        visible={showConfirmLost}
        onOK={onConfirmLost}
        onCancel={onCloseConfirmLost}
        okText={intl.formatMessage({ id: 'general.form.yes' })}
        cancelText={intl.formatMessage({ id: 'general.form.cancel' })}
        width="400px"
      >
        <h2>{intl.formatMessage({ id: 'application.lostsid.title' })}</h2>
        <p>
          {intl.formatMessage({ id: 'certificates.table.label.documentnumber' })}: {documentNumber}
        </p>
        <p>{intl.formatMessage({ id: 'application.lostsid.description' })}</p>
        <ReasonInput>
          <SdirInput
            placeholder={intl.formatMessage({ id: 'application.lostsid.reason' })}
            value={lostReason}
            type="text"
            onChange={(e: any) => setLostReason(e?.target?.value)}
          />
        </ReasonInput>
        {reportLoading && (
          <FeedbackWrapper>
            <Loader />
          </FeedbackWrapper>
        )}
        {reportError && (
          <FeedbackWrapper>
            <ErrorWithTrace
              error={reportError}
              trace={reportError?.response?.headers?.traceresponse}
            />
          </FeedbackWrapper>
        )}
      </SdirModal>
    </Wrapper>
  );
};

export default SidOverviewPage;

const Wrapper = styled.div`
  padding-top: 20px;
  padding-bottom: 60px;
  display: grid;
  .sectionheader {
    margin-top: 5em;
  }
  h1 {
    margin-bottom: 0;
  }
`;

const ReasonInput = styled.div`
  margin: 30px 0;
`;

const FeedbackWrapper = styled.div`
  margin-bottom: 30px;
`;

const ButtonRow = styled.div`
  display: flex;
`;

const TableWrapper = styled.div`
  margin-top: 40px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  button {
    font-size: 14px;
    line-height: 14px;

    &:last-child {
      margin-left: 10px;
      min-height: 24px;
      height: auto;
      width: max-content;
    }
  }
`;

const ErrorWrapper = styled.div`
  margin-top: 20px;
`;

const StyledDataTable = styled(DataTable)`
  margin-left: -5px;
  background-color: transparent;
  thead tr th {
    button {
      text-align: left;
    }
  }
  tbody > tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    & > td {
      padding-left: 14px;
      &:last-child {
        padding-right: 14px !important;
      }
    }

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }
  }
`;
